import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { apiHandler } from "../../../api";
import { endpoint } from "../../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";

const DEBOUNCE_DELAY = 500; // ms

export const useCounterPartyIssues = (type = "issues") => {
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = parseInt(searchParams.get("page"), 10);
  const initialPage = !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
  const limitFromUrl = parseInt(searchParams.get("limit"), 10);
  const initialLimit = !isNaN(limitFromUrl) && limitFromUrl > 0 ? limitFromUrl : 10;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [analytics, setAnalytics] = useState({});

  const [filters, setFilters] = useState({
    status: "",
    issueId: "",
    issue_level: "",
    issue_domain: "",
    resolution_type: "",
    sla_breach: "",
    created_from: "",
    created_to  : "",
    search_by: "",
    search_value: "",
    sort_order: "desc",
  });

  const [filterOptions, setFilterOptions] = useState({
    status: [],
    issue_level: [],
    issue_domain: [],
    resolution_type: [],
    sla_breach: [],
  });

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  const debounceTimer = useRef(null);

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      let url =
        type === "issues"
          ? endpoint.GET_ISSUES
          : `${endpoint.GET_GRIEVANCES}?issue_level=GRIEVANCE`;

      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim() !== "") {
          const cleanValue = value.toString().trim();

          if (key === "search_value" || key === "search_by") return; // handle search separately
          params.append(key, cleanValue);
        }
      });

      // handle search safely
      if (filters.search_by && filters.search_value) {
        const searchVal = filters.search_value.toString().trim();
        params.append(filters.search_by, searchVal);
      }

      // pagination
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      if (params.toString()) {
        url +=
          type === "issues" ? `?${params.toString()}` : `&${params.toString()}`;
      }

      const result = await apiHandler({
        url,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      if (result?.data?.rows) {
        setData(result.data.rows);
        setTotalCount(result.data.pagination?.total || 0);
      }

      if (result?.data?.filters) {
        setFilterOptions(result.data.filters);
      }
      if (result?.data?.analytics) {
  setAnalytics(result.data.analytics);
 } else {
  setAnalytics({}); // Clear if not present
 }
    } catch (err) {
      //console.error("Error fetching data:", err);
      setData([]);
      setTotalCount(0);
      setAnalytics({});
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage, authData, dispatch, storeId, type]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    setLoading(true);

    debounceTimer.current = setTimeout(() => {
      getData();
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(debounceTimer.current);
  }, [
    filters.search_value,
    filters.search_by,
    currentPage,
    itemsPerPage,
    getData,
  ]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = useCallback(
    (newPage) => {
      const pageNum = typeof newPage === "function" ? newPage(currentPage) : newPage;
      const validPage = Math.max(1, pageNum);
      setCurrentPage(validPage);
      const next = new URLSearchParams(window.location.search);
      if (validPage <= 1) {
        next.delete("page");
      } else {
        next.set("page", validPage.toString());
      }
      setSearchParams(next, { replace: true });
    },
    [currentPage, setSearchParams]
  );

  // Sync state if URL search params change
  useEffect(() => {
    const pageInUrl = parseInt(searchParams.get("page"), 10);
    const validPage = !isNaN(pageInUrl) && pageInUrl > 0 ? pageInUrl : 1;
    setCurrentPage((prev) => (prev !== validPage ? validPage : prev));

    const limitInUrl = parseInt(searchParams.get("limit"), 10);
    if (!isNaN(limitInUrl) && limitInUrl > 0) {
      setItemsPerPage((prev) => (prev !== limitInUrl ? limitInUrl : prev));
    }
  }, [searchParams]);

  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage) => {
      const limitNum = Number(newItemsPerPage);
      setItemsPerPage(limitNum);
      setCurrentPage(1);
      const next = new URLSearchParams(window.location.search);
      next.delete("page");
      if (limitNum === 10) {
        next.delete("limit");
      } else {
        next.set("limit", limitNum.toString());
      }
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  return {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage: handlePageChange,
    itemsPerPage,
    setItemsPerPage: handleItemsPerPageChange,
    totalPages,
    analytics,
  };
};




